// Single place the blog talks to the shared SEO API ("SEO Studio").
//
// Every blog route needs the same three things — the post list, the category nav, and one
// post — so the calls live here instead of being inlined per screen: the category segment is
// part of the canonical URL, and the rule for building it has to be identical on the listing,
// the post page, and the redirect from the old flat URL.
//
// Ported from NevTan Sign's src/utils/blogApi.js (docusignUi), pointed at the `nevtan_ai`
// product in the same shared SEO Studio CMS. That product is still being registered and may
// have zero posts for a while — every export here degrades to an empty/null value rather than
// throwing, exactly like Sign's `get()` helper, so the blog renders (as an empty state) long
// before real content exists.

export const SEO_API = 'https://seo-api.nevtan.com/api/public'
export const PROJECT = 'nevtan'
export const PRODUCT = 'nevtan_ai'

export const SITE_ORIGIN = 'https://ai.nevtan.com'

export const DEFAULT_LANGUAGE = 'en'

/** An author as the SEO API embeds it on a post. */
export interface BlogAuthor {
  name?: string | null
}

/** A tag as the SEO API returns it — sometimes a bare string, sometimes an object. */
export interface BlogTag {
  name: string
  slug?: string
}

/** A category, as returned both standalone (fetchCategories) and embedded on a post. */
export interface BlogCategory {
  slug: string
  name: string
  description?: string | null
}

/**
 * A blog post ("page" in the CMS's own vocabulary — every export here keeps that name where it
 * talks to the API, matching Sign's `fetchPages`/`fetchPage`).
 */
export interface BlogPost {
  slug: string
  title: string
  metaDescription?: string | null
  htmlContent?: string | null
  templateCss?: string | null
  heroImageUrl?: string | null
  thumbnailUrl?: string | null
  pageType?: string | null
  keyword?: string | null
  language?: string | null
  qualityScore?: number | null
  author?: BlogAuthor | null
  category?: BlogCategory | null
  tags?: (string | BlogTag)[] | null
  createdAt?: string | null
  approvedAt?: string | null
  publishedAt?: string | null
  updatedAt?: string | null
}

function buildQuery(params?: Record<string, string | null | undefined>): string {
  const query = new URLSearchParams({ product: PRODUCT })
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value != null && value !== '' && value !== 'all') query.set(key, value)
  })
  return query.toString()
}

/**
 * Unwraps the API's { success, data } envelope.
 *
 * @returns the payload, or `fallback` for any non-2xx, malformed, or failed response — the
 *          blog must render even when the SEO API is unreachable, or (until `nevtan_ai` is
 *          fully registered and authored) when it is reachable but simply has nothing yet.
 */
async function get<T>(path: string, fallback: T): Promise<T> {
  try {
    // No explicit cache option is needed here the way Sign's Next.js server-side fetch needed
    // `cache: "no-store"` — this app has no server-side fetch cache to fight (it is a pure
    // client-side SPA, every call runs in the browser) — but the browser's own HTTP cache is
    // skipped anyway for the same underlying reason Sign disables Next's: the /pages list
    // response embeds every post's full-resolution hero image as inline base64 and can run to
    // several megabytes, so a stale cached copy silently hiding newly published posts is a risk
    // worth avoiding even here.
    const res = await fetch(`${SEO_API}${path}`, { cache: 'no-store' })
    if (!res.ok) return fallback
    const body = (await res.json()) as { success?: boolean; data?: T }
    if (!body?.success || body.data == null) return fallback
    return body.data
  } catch {
    return fallback
  }
}

/**
 * Live posts, optionally narrowed to one category.
 *
 * The API also accepts a `language` filter, but the site does not offer a language switcher
 * yet — every post is served regardless of the language it is labelled with.
 */
export async function fetchPages({ category }: { category?: string | null } = {}): Promise<
  BlogPost[]
> {
  const data = await get<BlogPost[]>(`/${PROJECT}/pages?${buildQuery({ category })}`, [])
  return Array.isArray(data) ? data : []
}

/**
 * Categories that actually have a live post.
 *
 * The API filters these itself, which is the point: the nav only ever shows categories a blog
 * has been published into, so an empty category never appears on the site.
 */
export async function fetchCategories(): Promise<BlogCategory[]> {
  const data = await get<BlogCategory[]>(`/${PROJECT}/categories?${buildQuery()}`, [])
  return Array.isArray(data) ? data : []
}

/** A single post by slug. Returns null when it does not exist or is not published. */
export async function fetchPage(slug?: string | null): Promise<BlogPost | null> {
  if (!slug) return null
  return get<BlogPost | null>(`/${PROJECT}/pages/${encodeURIComponent(slug)}`, null)
}

/** Fire-and-forget signal that the site has picked the post up. */
export function acknowledgePage(slug?: string | null): void {
  if (!slug) return
  fetch(`${SEO_API}/${PROJECT}/pages/${encodeURIComponent(slug)}/acknowledge`, {
    method: 'POST',
  }).catch(() => {})
}

/**
 * The one canonical path for a post.
 *
 * A post with a category lives under it; one without keeps the flat `/blog/<slug>` URL. Both
 * the redirect from the old address and the `canonical` tag are derived from this, so a post
 * can never be indexed at two addresses.
 */
export function canonicalPathFor(page?: BlogPost | null): string {
  if (!page?.slug) return '/blog'
  const category = page.category?.slug
  return category ? `/blog/category/${category}/${page.slug}` : `/blog/${page.slug}`
}

export function canonicalUrlFor(page?: BlogPost | null): string {
  return `${SITE_ORIGIN}${canonicalPathFor(page)}`
}

/** Path of a category's own listing page. */
export function categoryPathFor(category?: BlogCategory | string | null): string {
  const slug = typeof category === 'string' ? category : category?.slug
  return slug ? `/blog/category/${slug}` : '/blog'
}

/**
 * The language a post is written in, as an ISO code.
 *
 * Only used for the `lang` attribute and the locale meta tags on a post — there is no language
 * switcher on the site, so nothing filters by it. Falls back to English, which is what a post
 * carries when nobody has set a language on it.
 */
export function languageOf(page?: BlogPost | null): string {
  const raw = page?.language
  if (!raw) return DEFAULT_LANGUAGE
  return String(raw).trim().toLowerCase().split('-')[0] || DEFAULT_LANGUAGE
}

export interface BlogListSchemaOptions {
  path?: string
  name?: string
  description?: string
}

/**
 * Blog/BlogPosting structured data for a listing page (all posts, or one category).
 *
 * Small local port of Sign's `blogListSchema()` (docusignUi's src/constant/seo.js) — this app
 * has no shared JsonLd builder file to reuse, so it lives here next to the rest of the blog
 * data layer, the same way Drive's own port keeps it in its `blogApi.js`.
 */
export function blogListSchema(
  pages: BlogPost[] = [],
  { path = '/blog', name, description }: BlogListSchemaOptions = {},
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    url: `${SITE_ORIGIN}${path}`,
    name: name || 'NevTan AI Blog',
    description:
      description ||
      'Guides on running local AI models privately, choosing hardware, and getting the most out of NevTan AI.',
    blogPost: (Array.isArray(pages) ? pages : [])
      .filter((p) => p?.title)
      .map((p) => ({
        '@type': 'BlogPosting',
        headline: p.title,
        url: canonicalUrlFor(p),
      })),
  }
}
